import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { PhoneLink } from '@/components/PhoneLink'
import { VinChip } from '@/components/VinChip'

import type { SearchResult } from './types'

const KIND_GLYPH: Record<SearchResult['kind'], { letter: string; bg: string }> = {
  customer: { letter: 'C', bg: 'bg-brand text-white' },
  vehicle: { letter: 'V', bg: 'bg-emerald-500 text-white' },
  appointment: { letter: 'A', bg: 'bg-amber-500 text-white' },
}

/**
 * Search result tiles. When `onPick` is provided, clicking a tile
 * fires it instead of navigating — used by the cascade builder so
 * picking a target advances the composer state. When `onPick` is
 * omitted (bare-typing mode), tiles navigate to the entity detail
 * page as a normal search-to-jump shortcut.
 *
 * Each tile is a `role="button"` div (rather than a <Link> or <button>)
 * so we can nest a real <a href="tel:"> for tap-to-call on customer
 * phones without invalid nested-interactive HTML. Enter/Space activates
 * the primary action for keyboard users.
 */
export function SearchResults({
  results,
  isLoading,
  query,
  onPick,
  // True while a fresh query is in flight and the tiles still show the
  // PREVIOUS query's results (kept by placeholderData upstream). We dim them
  // and ignore activation so a tap during the fetch can't pick a row that no
  // longer matches what was typed (the search-select race fix).
  stale = false,
}: {
  results: SearchResult[]
  isLoading: boolean
  query: string
  onPick?: (r: SearchResult) => void
  stale?: boolean
}) {
  const { t } = useTranslation('dashboard')
  const navigate = useNavigate()

  if (query.length === 0) return null

  return (
    <div
      aria-busy={stale}
      className={`mt-2 rounded-xl border border-border bg-card shadow-sm transition-opacity ${stale ? 'opacity-60' : ''}`}
    >
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="border-b border-border px-3 py-2 text-xs uppercase tracking-wide text-muted-foreground"
      >
        {isLoading
          ? t('agentic.search.searching')
          : t('agentic.search.results_count', { count: results.length })}
      </div>
      <ul
        className={`max-h-72 divide-y divide-border overflow-y-auto ${stale ? 'pointer-events-none' : ''}`}
      >
        {results.map((r) => {
          const glyph = KIND_GLYPH[r.kind]
          const activate = () => {
            if (stale) return
            if (onPick) onPick(r)
            else navigate(r.url)
          }
          return (
            <li key={`${r.kind}-${r.id}`}>
              <div
                role="button"
                tabIndex={0}
                onClick={activate}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    activate()
                  }
                }}
                className="flex min-h-14 cursor-pointer items-center gap-3 px-3 py-2 text-left text-sm transition hover:bg-muted focus:outline-none focus:ring-2 focus:ring-brand/30"
              >
                <span
                  className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-xs font-semibold ${glyph.bg}`}
                  aria-hidden
                >
                  {glyph.letter}
                </span>
                <span className="min-w-0 flex-1 text-left">
                  <span className="block truncate font-medium text-foreground">
                    {r.name}
                  </span>
                  {r.phone || r.meta ? (
                    <span className="block truncate text-xs text-muted-foreground">
                      {r.phone ? (
                        <PhoneLink
                          phone={r.phone}
                          className="text-muted-foreground"
                        />
                      ) : null}
                      {r.phone && r.meta ? ' · ' : null}
                      {r.meta}
                    </span>
                  ) : null}
                  {r.vin ? (
                    <span className="mt-1 block">
                      <VinChip vin={r.vin} size="xs" />
                    </span>
                  ) : null}
                </span>
                <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                  {t(`agentic.search.kind.${r.kind}`)}
                </span>
              </div>
            </li>
          )
        })}
        {!isLoading && results.length === 0 ? (
          <li className="px-3 py-4 text-sm text-muted-foreground">
            {t('agentic.search.empty', { query })}
          </li>
        ) : null}
      </ul>
    </div>
  )
}
